/**
 * Page-level allow/block overrides for the signed-in user.
 *
 * `/init` returns `pageOverridesByOrg: { [org]: { allow: [path], block: [path] } }`
 * from the `dashboard_org_pages` table. The code baseline (router.tsx org
 * blocks, captured in the generated catalogue) stays authoritative; overrides
 * sit on top:
 *
 *   visible(path) = ∃ org ∈ userOrgs :
 *                     (baseline(org, path) ∧ path ∉ block(org)) ∨ path ∈ allow(org)
 *
 * Per-org before the union, so an allow in one of the user's orgs is never
 * cancelled by a block in another. Sentinel rows are ignored (the backend
 * never writes them).
 */
import { useMemo } from 'react'
import { useAuthUserData, useUserOrganisations } from '../authUserProvider'
import { OVERRIDE_EXEMPT_ORGANISATIONS, PAGE_CATALOGUE, isBaselinePage } from './pageCatalogue'

export interface OrgPageOverrides {
    allow: string[]
    block: string[]
}

export type PageOverridesByOrg = Record<string, OrgPageOverrides>

export const EMPTY_PAGE_OVERRIDES: PageOverridesByOrg = {}

/** Defensive parse of whatever `/init` or localStorage hands us. */
export const normalisePageOverrides = (raw: unknown): PageOverridesByOrg => {
    if (!raw || typeof raw !== 'object') return {}
    const out: PageOverridesByOrg = {}
    Object.entries(raw as Record<string, any>).forEach(([org, value]) => {
        if (!org || !value || typeof value !== 'object') return
        const allow = Array.isArray(value.allow) ? value.allow.filter((p: unknown) => typeof p === 'string') : []
        const block = Array.isArray(value.block) ? value.block.filter((p: unknown) => typeof p === 'string') : []
        if (allow.length || block.length) out[org] = { allow, block }
    })
    return out
}

export interface PageAccess {
    /** Pages granted by an `allow` row in at least one of the user's orgs. */
    allowedPages: string[]
    /** Pages the user cannot see even though an org block mounts them. */
    blockedPages: string[]
    /** True when the page must be hidden from the sidebar and not mounted. */
    isBlocked: (path: string) => boolean
    /** True when the page is only reachable because of an `allow` row. */
    isAllowedExtra: (path: string) => boolean
}

/** Pure form of the hook, for the router/sidebar and for tests. */
export const resolvePageAccess = (organisations: string[], overrides: PageOverridesByOrg): PageAccess => {
    const orgs = organisations.filter(Boolean)
    const allowed = new Set<string>()
    const blocked = new Set<string>()

    orgs.forEach((org) => {
        if (OVERRIDE_EXEMPT_ORGANISATIONS.includes(org)) return
        overrides[org]?.allow.forEach((p) => allowed.add(p))
    })

    Object.keys(PAGE_CATALOGUE).forEach((path) => {
        if (allowed.has(path)) return
        const mountingOrgs = orgs.filter((org) => isBaselinePage(org, path))
        if (mountingOrgs.length === 0) return
        const everyMountingOrgBlocks = mountingOrgs.every(
            (org) => !OVERRIDE_EXEMPT_ORGANISATIONS.includes(org) && (overrides[org]?.block || []).includes(path),
        )
        if (everyMountingOrgBlocks) blocked.add(path)
    })

    // A page the user has by baseline in some org is not an "extra".
    const extras = Array.from(allowed).filter((path) => !orgs.some((org) => isBaselinePage(org, path)))

    return {
        allowedPages: extras,
        blockedPages: Array.from(blocked),
        isBlocked: (path) => blocked.has(path),
        isAllowedExtra: (path) => extras.includes(path),
    }
}

/**
 * Hook used by the router, sidebar and route guard. Falls back to the raw
 * Cognito org (like `canSeeWithFallback`) so the very first render after a
 * hard refresh resolves the same way as once `/init` state rehydrates.
 */
export const usePageAccess = (): PageAccess => {
    const { organisations, pageOverridesByOrg } = useUserOrganisations()
    const authUser = useAuthUserData()
    const fallbackOrg: string = authUser?.['custom:organisation'] || ''
    const orgs = organisations.length > 0 ? organisations : [fallbackOrg].filter(Boolean)
    const orgsKey = orgs.join('|')
    return useMemo(
        () => resolvePageAccess(orgsKey ? orgsKey.split('|') : [], pageOverridesByOrg || EMPTY_PAGE_OVERRIDES),
        [orgsKey, pageOverridesByOrg],
    )
}
